import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  CheckCircle2, Circle, Truck, UserX, RefreshCw, Map as MapIcon, List as ListIcon, Phone, MapPin, Home,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import DriverRouteMap from '@/components/DriverRouteMap';
import API from '@/config/api';

const STATUS_META = {
  waiting: { label: 'Waiting', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Circle },
  onboard: { label: 'On Van', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
};

const StatusBadge = ({ status, notRiding }) => {
  if (notRiding) {
    return (
      <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">
        <UserX className="w-3 h-3 mr-1" /> Absent
      </Badge>
    );
  }
  const meta = STATUS_META[status] || STATUS_META.waiting;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const DriverLiveRouteDialog = ({ open, onClose, driver }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState({ children: [], manifest: {}, date: '' });
  const [view, setView] = useState('list'); // list | map
  const [direction, setDirection] = useState('pickup');

  const load = useCallback(async () => {
    if (!driver?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/drivers/${driver.id}/live-route`, getAuthHeaders());
      setRoute(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load live route');
    } finally {
      setLoading(false);
    }
  }, [driver, getAuthHeaders]);

  useEffect(() => {
    if (open) load();
     
  }, [open, driver?.id]);

  const active = route.children.filter((c) => !route.manifest[c.id]?.not_riding);
  const total = active.length;
  const onboard = active.filter((c) => route.manifest[c.id]?.status === 'onboard').length;
  const delivered = active.filter((c) => route.manifest[c.id]?.status === 'delivered').length;
  const absent = route.children.length - total;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="driver-live-route-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-primary" />
            Live Route — {driver?.name}
          </DialogTitle>
          <DialogDescription>
            Read-only view of {driver?.name?.split(' ')[0]}&apos;s van manifest as of {route.date || 'today'}.
          </DialogDescription>
        </DialogHeader>

        {/* Direction toggle */}
        <div className="bg-white rounded-lg border border-border p-1 flex" data-testid="live-direction-toggle">
          <button
            onClick={() => setDirection('pickup')}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
              direction === 'pickup' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
            }`}
            data-testid="live-direction-pickup"
          >
            AM Pickup → Center
          </button>
          <button
            onClick={() => setDirection('dropoff')}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
              direction === 'dropoff' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
            }`}
            data-testid="live-direction-dropoff"
          >
            PM Dropoff → Home
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2">
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <div className="text-xl font-bold" data-testid="live-stat-riding">{total}</div>
            <div className="text-xs text-muted-foreground">Riding</div>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg text-center">
            <div className="text-xl font-bold text-amber-700" data-testid="live-stat-onboard">{onboard}</div>
            <div className="text-xs text-muted-foreground">On Van</div>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg text-center">
            <div className="text-xl font-bold text-emerald-700" data-testid="live-stat-delivered">{delivered}</div>
            <div className="text-xs text-muted-foreground">Delivered</div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <div className="text-xl font-bold text-slate-500" data-testid="live-stat-absent">{absent}</div>
            <div className="text-xs text-muted-foreground">Absent</div>
          </div>
        </div>

        {/* View + refresh */}
        <div className="flex gap-2 items-center">
          <div className="bg-white rounded-lg border border-border p-1 flex flex-1" data-testid="live-view-toggle">
            <button
              onClick={() => setView('list')}
              className={`flex-1 py-1.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-1.5 ${
                view === 'list' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
              }`}
              data-testid="live-view-list"
            >
              <ListIcon className="w-4 h-4" /> List
            </button>
            <button
              onClick={() => setView('map')}
              className={`flex-1 py-1.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-1.5 ${
                view === 'map' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
              }`}
              data-testid="live-view-map"
            >
              <MapIcon className="w-4 h-4" /> Map
            </button>
          </div>
          <Button
            size="icon"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="live-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading…</div>
        ) : route.children.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="live-empty">
            No children assigned to this van.
          </div>
        ) : view === 'map' ? (
          <DriverRouteMap
            stops={route.children.filter((c) => !route.manifest[c.id]?.not_riding)}
            direction={direction}
          />
        ) : (
          <div className="space-y-2" data-testid="live-list">
            {route.children.map((c) => {
              const m = route.manifest[c.id] || {};
              const status = m.status || 'waiting';
              const notRiding = !!m.not_riding;
              const address = direction === 'pickup' ? c.pickup_address : c.dropoff_address;
              const label = direction === 'pickup' ? c.pickup_label : c.dropoff_label;
              return (
                <div
                  key={c.id}
                  className={`border border-border rounded-lg p-3 flex items-start gap-3 ${notRiding ? 'opacity-60' : ''}`}
                  data-testid={`live-row-${c.id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {c.stop_order}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-semibold">{c.display_name}</div>
                      <StatusBadge status={status} notRiding={notRiding} />
                    </div>
                    {label && <div className="text-xs text-primary font-medium">{direction === 'pickup' ? 'Pick up from' : 'Drop off at'}: {label}</div>}
                    {address && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {direction === 'pickup' ? <Home className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                        <span className="truncate">{address}</span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      {c.classroom && <Badge variant="secondary" className="text-xs">{c.classroom}</Badge>}
                      {c.parent_name && <span>Parent: <span className="text-foreground">{c.parent_name}</span></span>}
                      {c.parent_phone && (
                        <a href={`tel:${c.parent_phone}`} className="text-primary underline inline-flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {c.parent_phone}
                        </a>
                      )}
                    </div>
                    {(m.onboard_at || m.delivered_at) && (
                      <div className="text-xs text-muted-foreground">
                        {m.onboard_at && <>On van: {new Date(m.onboard_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · </>}
                        {m.delivered_at && <>Delivered: {new Date(m.delivered_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverLiveRouteDialog;
